/**
 * Bull Kicks Wishlist — self-hosted, localStorage-based.
 *
 * Storage: localStorage key `bk_wl` = [{ handle, title, price, compareAt, image, url, vendor, addedAt }]
 * Max items: 50 (FIFO eviction)
 *
 * API exposed on window.BKWishlist:
 *   add(item), remove(handle), toggle(item), has(handle), list(), count(), clear()
 *
 * DOM hooks:
 *   [data-bk-wl-btn]             heart button (auto-bound, aria-pressed reflects state)
 *   [data-bk-wl-count]            counter badge (text content = count)
 *   [data-bk-wl-open]             opens the drawer
 *   [data-bk-wl-close]            closes the drawer
 *   [data-bk-wl-drawer]           drawer root (must have `is-open` class when open)
 *   [data-bk-wl-list]             drawer list container (rendered from state)
 *   [data-bk-wl-empty]            shown when count === 0
 *
 * Events dispatched on document:
 *   bk:wl:changed { detail: { count, added, removed } }
 */
(function () {
  var KEY = 'bk_wl';
  var MAX = 50;

  // i18n: labels injected by bk-wishlist-drawer.liquid as window.BK_I18N.wishlist_button.
  // Read lazily (at call time) so script load order vs. injection never matters; Chinese fallback.
  function bkWlI18n() { return (window.BK_I18N && window.BK_I18N.wishlist_button) || {}; }

  // A136: strip vendor prefix from title, preserving digit-after model names
  function bkStripVendorPrefix(title, vendor) {
    if (!title || !vendor) return title || '';
    var tl = title.toLowerCase();
    var vl = vendor.toLowerCase();
    if (tl.slice(0, vl.length) !== vl) return title;
    var rest = title.slice(vendor.length).replace(/^\s+/, '');
    var first = rest.charAt(0);
    if (first >= '0' && first <= '9') return title;
    return rest;
  }

  function read() {
    try {
      var raw = localStorage.getItem(KEY);
      var list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list : [];
    } catch (e) { return []; }
  }
  function write(list) {
    try { localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX))); } catch (e) {}
  }
  function emit(detail) {
    document.dispatchEvent(new CustomEvent('bk:wl:changed', { detail: detail }));
  }

  var api = {
    list: function () { return read(); },
    count: function () { return read().length; },
    has: function (handle) { return read().some(function (i) { return i.handle === handle; }); },
    add: function (item) {
      if (!item || !item.handle) return false;
      var list = read();
      if (list.some(function (i) { return i.handle === item.handle; })) return false;
      item.addedAt = Date.now();
      list.unshift(item);
      if (list.length > MAX) list = list.slice(0, MAX);
      write(list);
      emit({ count: list.length, added: item.handle });
      return true;
    },
    remove: function (handle) {
      var list = read();
      var next = list.filter(function (i) { return i.handle !== handle; });
      if (next.length === list.length) return false;
      write(next);
      emit({ count: next.length, removed: handle });
      return true;
    },
    toggle: function (item) {
      if (!item || !item.handle) return 'noop';
      return api.has(item.handle) ? (api.remove(item.handle), 'removed') : (api.add(item), 'added');
    },
    clear: function () {
      write([]);
      emit({ count: 0, cleared: true });
    },
  };

  window.BKWishlist = api;

  // -----------------------------------------------------------------
  // DOM wiring
  // -----------------------------------------------------------------

  // BK_PRICE_FMT_v1 — match shop money_without_trailing_zeros format ($X,XXX, or $X,XXX.XX only when cents are non-zero) instead of hardcoded HK$ prefix
  function money(cents) {
    if (typeof cents !== 'number') return '';
    var hasCents = cents % 100 !== 0;
    var amount = hasCents ? (cents / 100).toFixed(2) : String(Math.round(cents / 100));
    var amountParts = amount.split('.');
    var formattedWhole = amountParts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return amountParts.length > 1 ? '$' + formattedWhole + '.' + amountParts[1] : '$' + formattedWhole;
  }

  function syncButton(btn) {
    var handle = btn.getAttribute('data-bk-wl-handle');
    if (!handle) return;
    var active = api.has(handle);
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    btn.classList.toggle('is-active', active);
    var label = btn.querySelector('[data-bk-wl-btn-label]');
    if (label) { var t = bkWlI18n(); label.textContent = active ? (t.added || '已加入心願單') : (t.add || '加入心願單'); }
  }

  function syncAllButtons() {
    document.querySelectorAll('[data-bk-wl-btn]').forEach(syncButton);
  }

  function syncCounts() {
    var c = api.count();
    document.querySelectorAll('[data-bk-wl-count]').forEach(function (el) {
      el.textContent = c > 0 ? String(c) : '';
      el.hidden = c === 0;
    });
  }

  function renderDrawer() {
    var drawer = document.querySelector('[data-bk-wl-drawer]');
    if (!drawer) return;
    var list = api.list();
    var listEl = drawer.querySelector('[data-bk-wl-list]');
    var emptyEl = drawer.querySelector('[data-bk-wl-empty]');
    if (list.length === 0) {
      if (listEl) listEl.innerHTML = '';
      if (emptyEl) emptyEl.hidden = false;
      return;
    }
    if (emptyEl) emptyEl.hidden = true;
    if (!listEl) return;
    var frag = document.createDocumentFragment();
    list.forEach(function (item) {
      var row = document.createElement('div');
      row.className = 'bk-wl__item';
      row.setAttribute('data-handle', item.handle);

      var a = document.createElement('a');
      a.href = item.url;
      a.className = 'bk-wl__link';

      var img = document.createElement('img');
      img.src = item.image || '';
      img.alt = item.title || '';
      img.loading = 'lazy';
      img.className = 'bk-wl__img';

      var info = document.createElement('div');
      info.className = 'bk-wl__info';
      if (item.vendor) {
        var v = document.createElement('div');
        v.className = 'bk-wl__vendor';
        v.textContent = item.vendor;
        info.appendChild(v);
      }
      var t = document.createElement('div');
      t.className = 'bk-wl__title';
      t.textContent = bkStripVendorPrefix(item.title || '', item.vendor || '');
      info.appendChild(t);
      var p = document.createElement('div');
      p.className = 'bk-wl__price';
      p.textContent = money(item.price);
      info.appendChild(p);

      a.appendChild(img);
      a.appendChild(info);
      row.appendChild(a);

      var rm = document.createElement('button');
      rm.type = 'button';
      rm.className = 'bk-wl__remove';
      rm.setAttribute('aria-label', bkWlI18n().remove || '移除');
      rm.setAttribute('data-bk-wl-remove', item.handle);
      rm.innerHTML = '&times;';
      row.appendChild(rm);

      frag.appendChild(row);
    });
    listEl.innerHTML = '';
    listEl.appendChild(frag);
  }

  function openDrawer() {
    var d = document.querySelector('[data-bk-wl-drawer]');
    if (!d) return;
    renderDrawer();
    d.removeAttribute('hidden');
    requestAnimationFrame(function () { d.classList.add('is-open'); });
    document.body.style.overflow = 'hidden';
  }
  function closeDrawer() {
    var d = document.querySelector('[data-bk-wl-drawer]');
    if (!d) return;
    d.classList.remove('is-open');
    document.body.style.overflow = '';
    setTimeout(function () { if (!d.classList.contains('is-open')) d.setAttribute('hidden', ''); }, 250);
  }

  // Global click delegation
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-bk-wl-btn]');
    if (btn) {
      e.preventDefault();
      e.stopPropagation();
      var handle = btn.getAttribute('data-bk-wl-handle');
      if (!handle) return;
      var item = {
        handle: handle,
        title: btn.getAttribute('data-bk-wl-title') || '',
        price: parseInt(btn.getAttribute('data-bk-wl-price'), 10) || 0,
        compareAt: parseInt(btn.getAttribute('data-bk-wl-compare'), 10) || null,
        image: btn.getAttribute('data-bk-wl-image') || '',
        url: btn.getAttribute('data-bk-wl-url') || '',
        vendor: btn.getAttribute('data-bk-wl-vendor') || '',
      };
      api.toggle(item);
      return;
    }
    if (e.target.closest('[data-bk-wl-open]')) { e.preventDefault(); openDrawer(); return; }
    if (e.target.closest('[data-bk-wl-close]') || e.target.matches('[data-bk-wl-drawer]')) {
      closeDrawer();
      return;
    }
    var rm = e.target.closest('[data-bk-wl-remove]');
    if (rm) {
      e.preventDefault();
      api.remove(rm.getAttribute('data-bk-wl-remove'));
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var d = document.querySelector('[data-bk-wl-drawer].is-open');
    if (d) closeDrawer();
  });

  document.addEventListener('bk:wl:changed', function () {
    syncAllButtons();
    syncCounts();
    if (document.querySelector('[data-bk-wl-drawer].is-open')) renderDrawer();
  });

  // Initial sync on load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { syncAllButtons(); syncCounts(); });
  } else {
    syncAllButtons();
    syncCounts();
  }
})();
